import { Box, Chip, Grid } from "~/common/mui";
import { Card } from "~/features/configurator/Linear/components/Card";
import type { AccountInfoData } from "~/features/b2bAccountSelection/components/AccountSelectionTabelView/api/GetAccountList";
import classes from "~/features/b2bAccountSelection/components/AccountSelectionView/components/AccountSelectionGrid.module.less";
import classNames from "classnames";
import { Icon } from "~/common/components/icon/Icon";
import { IconType } from "~/common/components/icon/IconType";
import { C_ON_PRIMARY_CONTAINER, C_PRIMARY_CONTAINER } from "~/common/components/icon/Colors";
import Parameter from "~/common/components/Parameter";
import { useTranslation } from "react-i18next";
import { NoResultsFound } from "~/pages/AccountSelectionPage/AccountSelectionPage";
import { useIsMinDesktopSizeL } from "~/common/hooks/useMediaQuery";

interface NewAccountSelectionGridProps {
  accounts: AccountInfoData[];
  selectedAccount: AccountInfoData | undefined;
  onAccountClick: (account: AccountInfoData) => void;
  className?: string;
}

function isSchaefflerEmail(email?: string): boolean {
  if (!email) {
    return false;
  }

  const parts = email.toLowerCase().split("@");
  if (parts.length !== 2) {
    return false;
  }

  const domain = parts[1];
  return domain === "schaeffler.com";
}

const AccountTile = ({
  account,
  selected,
  onClick,
}: {
  account: AccountInfoData;
  selected: boolean;
  onClick: () => void;
}) => {
  const { t } = useTranslation();
  const email = window.SCHA?.user.email;

  return (
    <Grid
      key={account.id}
      size={{
        phone: 12,
        tablet: 6,
        "desktop-s": 4,
      }}
    >
      <Card className={classNames(classes.accountTile, selected && classes.selected)} onClick={onClick}>
        <div className={classNames(classes.container, selected && classes.selected)}>
          <div className={classes.name}>{account.name}</div>
          {selected && (
            <div className={classes.select}>
              <Chip
                sx={{
                  borderRadius: "8px",
                  padding: "0 8px",
                  background: C_PRIMARY_CONTAINER,
                  color: C_ON_PRIMARY_CONTAINER,
                }}
                label={t("accountSelection.selected")}
                icon={<Icon type={IconType.Check} color="success" />}
              />
            </div>
          )}
          <div className={classes.id}>
            <Parameter label={t("accountSelectionPage.account")}>{account.number}</Parameter>
          </div>
          {isSchaefflerEmail(email) && (
            <div className={classes.salesOrg}>
              <Parameter label={t("accountSelectionPage.salesOrg")}>{account.salesOrg}</Parameter>
            </div>
          )}
          <div className={classes.currency}>
            <Parameter label={t("page.accountSelection.tile.currency")}>{account.currency}</Parameter>
          </div>
        </div>
      </Card>
    </Grid>
  );
};

const NewAccountSelectionGrid = ({
  accounts,
  selectedAccount,
  onAccountClick,
  className,
}: NewAccountSelectionGridProps) => {
  const { t } = useTranslation();
  const isDesktopL = useIsMinDesktopSizeL();
  return (
    <Box>
      <Grid
        container
        spacing={isDesktopL ? 4 : 3}
        sx={{
          alignItems: "stretch",
          alignContent: "stretch",
          minHeight: "100%",
        }}
      >
        {accounts.map((account) => (
          <AccountTile
            key={account.id}
            account={account}
            selected={account === selectedAccount}
            onClick={() => onAccountClick(account)}
          />
        ))}
        {accounts.length === 0 && <NoResultsFound />}
      </Grid>
    </Box>
  );
};

export default NewAccountSelectionGrid;
